"use client";

import type { CaptionHighlightMode } from "@teksta/shared-types";
import { renderWithOverlay } from "mediatools";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";
import { type Cue, parseToCues } from "subtitletools";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { CAPTION_PRESETS, type CaptionPreviewSpec, createCaptionOverlay } from "./captions";
import { baseName, formatClock } from "./format";
import { MediaDropzone } from "./MediaDropzone";
import { OptionRadioGroup } from "./OptionRadioGroup";

const SUBTITLE_ACCEPT = {
  "application/x-subrip": [".srt"],
  "text/vtt": [".vtt"],
  "text/plain": [".srt", ".vtt"],
};

/**
 * Optional "find a line, clip it" panel: drop a subtitle file (SRT/VTT), search
 * the cues, and click one to set the clip to it — shift-click to span several.
 * Cues currently inside the clip are highlighted, as is the one at the playhead.
 *
 * Decoupled from the clip editor: it reports a chosen range through
 * `onPickRange` and reads the playhead via `getPreviewElement`.
 */
export function SubtitlePanel({
  getPreviewElement,
  setCaptionPreview,
  onPickRange,
  file,
  start,
  end,
  disabled = false,
}: {
  getPreviewElement?: () => HTMLVideoElement | HTMLAudioElement | null;
  setCaptionPreview?: (spec: CaptionPreviewSpec | null) => void;
  onPickRange: (start: number, end: number) => void;
  /** Source video, for burning captions into a new export. */
  file?: File | null;
  start: number;
  end: number;
  disabled?: boolean;
}) {
  const t = useTranslations("mediaTools.subtitlePanel");
  const [cues, setCues] = useState<Cue[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [current, setCurrent] = useState(0);
  const [burnProgress, setBurnProgress] = useState<number | null>(null);
  const [burnError, setBurnError] = useState<string | null>(null);
  const [presetId, setPresetId] = useState(CAPTION_PRESETS[0]?.id ?? "classic");
  const [highlightMode, setHighlightMode] = useState<CaptionHighlightMode>("none");
  const anchor = useRef<Cue | null>(null);

  // The style actually rendered: the chosen preset with the (orthogonal)
  // highlight mode applied. Shared by the live preview and the burn so they match.
  const effectiveStyle = useMemo(() => {
    const preset = (CAPTION_PRESETS.find((p) => p.id === presetId) ?? CAPTION_PRESETS[0])?.style;
    if (!preset) return null;
    return { ...preset, highlight: { ...preset.highlight, mode: highlightMode } };
  }, [presetId, highlightMode]);

  const load = async (file: File) => {
    setError(null);
    try {
      const parsed = parseToCues(await file.text());
      if (parsed.length === 0) {
        setError(t("errors.noSubtitles"));
        return;
      }
      setCues(parsed);
    } catch {
      setError(t("errors.couldNotRead"));
    }
  };

  // Track the playhead (coarse is fine) to highlight the current cue.
  useEffect(() => {
    const el = getPreviewElement?.();
    if (!el) return;
    const sync = () => setCurrent(el.currentTime);
    sync();
    el.addEventListener("timeupdate", sync);
    el.addEventListener("seeked", sync);
    return () => {
      el.removeEventListener("timeupdate", sync);
      el.removeEventListener("seeked", sync);
    };
  }, [getPreviewElement]);

  // Drive the live styled-caption overlay on the preview (same renderer as the
  // burn), updating as the cues or chosen style/highlight change.
  useEffect(() => {
    if (!setCaptionPreview) return;
    setCaptionPreview(cues && effectiveStyle ? { cues, style: effectiveStyle } : null);
    return () => setCaptionPreview(null);
  }, [cues, effectiveStyle, setCaptionPreview]);

  const filtered = useMemo(() => {
    if (!cues) return [];
    const q = query.trim().toLowerCase();
    if (!q) return cues;
    return cues.filter((cue) => cue.text.toLowerCase().includes(q));
  }, [cues, query]);

  const pick = (cue: Cue, extend: boolean) => {
    if (extend && anchor.current) {
      const a = anchor.current;
      onPickRange(Math.min(a.start, cue.start), Math.max(a.end, cue.end));
    } else {
      anchor.current = cue;
      onPickRange(cue.start, cue.end);
    }
  };

  const burnIntoVideo = async () => {
    if (!file || !cues || !effectiveStyle) return;
    setBurnError(null);
    setBurnProgress(0);
    try {
      const out = await renderWithOverlay(file, {
        format: "mp4",
        trim: { start, end }, // the clip editor's in/out
        onProgress: (p) => setBurnProgress(p),
        drawOverlay: createCaptionOverlay(cues, effectiveStyle),
      });
      const url = URL.createObjectURL(new Blob([out.data], { type: out.mimeType }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${baseName(file.name)}-captioned.${out.extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (cause) {
      setBurnError(cause instanceof Error ? cause.message : t("errors.couldNotBurn"));
    } finally {
      setBurnProgress(null);
    }
  };

  if (!cues) {
    return (
      <div className="space-y-2">
        <MediaDropzone
          accept={SUBTITLE_ACCEPT}
          prompt={t("dropPrompt")}
          disabled={disabled}
          onDrop={(files) => files[0] && load(files[0])}
        />
        {error && (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <input
          type="search"
          value={query}
          disabled={disabled}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("searchPlaceholder")}
          className="w-full rounded border bg-background px-3 py-1.5 text-sm outline-hidden disabled:opacity-50"
        />
        <button
          type="button"
          className="shrink-0 text-foreground/50 text-xs hover:text-foreground/80"
          onClick={() => {
            setCues(null);
            setQuery("");
            anchor.current = null;
          }}
        >
          {t("changeFile")}
        </button>
      </div>

      <ul className="max-h-80 divide-y overflow-auto rounded border">
        {filtered.map((cue) => {
          const inClip = cue.start < end && cue.end > start;
          const atPlayhead = current >= cue.start && current < cue.end;
          return (
            <li key={`${cue.start}-${cue.end}-${cue.text}`}>
              <button
                type="button"
                disabled={disabled}
                onClick={(event) => pick(cue, event.shiftKey)}
                className={cn(
                  "flex w-full items-start gap-3 px-3 py-2 text-left text-sm hover:bg-foreground/5",
                  inClip && "bg-foreground/10",
                  atPlayhead && "ring-1 ring-primary/50 ring-inset"
                )}
              >
                <span className="w-14 shrink-0 pt-0.5 font-mono text-foreground/50 text-xs">
                  {formatClock(cue.start)}
                </span>
                <span className="min-w-0 flex-1">{cue.text}</span>
              </button>
            </li>
          );
        })}
        {filtered.length === 0 && (
          <li className="px-3 py-2 text-foreground/50 text-sm">{t("noMatchingLines")}</li>
        )}
      </ul>

      <p className="text-foreground/50 text-xs">
        {t("pickHelpBeforeShift")} <kbd className="font-mono">Shift</kbd> {t("pickHelpAfterShift")}
      </p>

      {file && (
        <div className="space-y-2 border-t pt-3">
          {burnProgress === null && (
            <>
              <OptionRadioGroup
                name="caption-style"
                label={t("captionStyleLabel")}
                value={presetId}
                onChange={setPresetId}
                disabled={disabled}
                options={CAPTION_PRESETS.map((p) => ({
                  value: p.id,
                  label: t(`captionPresets.${p.id}`),
                }))}
              />
              <OptionRadioGroup<CaptionHighlightMode>
                name="caption-highlight"
                label={t("wordHighlightLabel")}
                value={highlightMode}
                onChange={setHighlightMode}
                disabled={disabled}
                options={[
                  { value: "none", label: t("highlight.none") },
                  {
                    value: "basic",
                    label: t("highlight.basic.label"),
                    hint: t("highlight.basic.hint"),
                  },
                  {
                    value: "creeping",
                    label: t("highlight.creeping.label"),
                    hint: t("highlight.creeping.hint"),
                  },
                  {
                    value: "sliding",
                    label: t("highlight.sliding.label"),
                    hint: t("highlight.sliding.hint"),
                  },
                ]}
              />
            </>
          )}
          {burnProgress === null ? (
            <Button type="button" size="sm" disabled={disabled} onClick={burnIntoVideo}>
              {t("burnButton")}
            </Button>
          ) : (
            <div className="space-y-1">
              <Progress value={Math.round(burnProgress * 100)} className="w-full" />
              <p className="text-foreground/60 text-xs">
                {t("burning", { progress: Math.round(burnProgress * 100) })}
              </p>
            </div>
          )}
          <p className="text-foreground/40 text-xs">
            {t("exportHelp", { start: formatClock(start), end: formatClock(end) })}
          </p>
          {burnError && (
            <p className="text-destructive text-sm" role="alert">
              {burnError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
